import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Typography from '@material-ui/core/Typography';
import { deactivateBindingAndGetSavedCards, setSaveCard } from '../../redux/modules/payment';
import { PAYMENT_METHOD } from '../../constants';
import useDeviceResize from '../../hooks/useDeviceResize';
import { getCardIcon } from '../../helpers/paymentHelper';
import idramLogo from '../../img/svgs/payment/idram.svg';
import cardTypes from '../../img/svgs/payment/cardTypes.svg';
import giftIcon from '../../img/svgs/payment/giftNew.png';

import styles from './PaymentMethodPicker.module.scss';

// classNames
const {
  paymentMethodPicker,
  paymentMethodContainer,
  paymentMethod,
  selected,
  disabled,
  error,
  redBorder,
  ameriaCardMethod,
} = styles;

const cardIcons = (
  <div className={styles.iconWrapper}>
    <div className={styles.icon}>
      <img alt="card" src={cardTypes} />
    </div>
  </div>
);

const idramIcon = (
  <div className={styles.iconWrapper}>
    <div className={styles.icon}>
      <img alt="idram" src={idramLogo} />
    </div>
  </div>
);

const formatExpirationString = (exp) => `${exp.slice(-2)}/${exp.slice(0, 4)}`;

const PaymentMethodPicker = (props) => {
  const {
    selectMethod,
    selectedMethod,
    savedCards,
    isValid,
    isFreePayment,
    isAutomaticRecurringPayment,
    isPolicyAttachCard,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDeviceMdSize = useDeviceResize('md');

  const hideIdram = useCallback(
    (list, hide) => (
      hide ? list.filter((item) => item.label !== PAYMENT_METHOD.IDRAM) : list
    ), [],
  );

  const [paymentMethodList, setPaymentMethodList] = useState([
    {
      label: PAYMENT_METHOD.CARD,
      title: savedCards.length
        ? 'paymentMethods.cardToCardNew'
        : 'paymentMethods.cardToCard',
      icon: cardIcons,
    },
    {
      label: PAYMENT_METHOD.IDRAM,
      title: 'paymentMethods.idram',
      icon: idramIcon,
    },
  ]);

  useEffect(() => {
    setPaymentMethodList((prevList) => hideIdram(prevList, isAutomaticRecurringPayment));
  }, [isAutomaticRecurringPayment, hideIdram]);

  useEffect(() => {
    setPaymentMethodList((prevList) => hideIdram(prevList, isPolicyAttachCard));
  }, [isPolicyAttachCard, hideIdram]);

  useEffect(() => {
    if (selectedMethod !== PAYMENT_METHOD.CARD) {
      dispatch(setSaveCard(false));
    }
    if (isAutomaticRecurringPayment) {
      dispatch(setSaveCard(true));
    }
  }, [dispatch, selectedMethod, isAutomaticRecurringPayment]);

  const cardRemoveHandler = (event, card) => {
    event.stopPropagation();
    dispatch(deactivateBindingAndGetSavedCards(card.cardHolderID));
    if (selectedMethod && selectedMethod.savedCard === card.cardHolderID) {
      selectMethod(null);
    }
  };

  if (isFreePayment) {
    const isFreeLabel = PAYMENT_METHOD.FREE;

    selectMethod(isFreeLabel);

    return (
      <div
        key={isFreeLabel}
        role="button"
        tabIndex="0"
        className={cn({
          [paymentMethod]: true,
          [styles.isFreePaymentMethod]: true,
        })}
      >
        <div className={styles.isFreeContainer}>
          <img alt="gift" src={giftIcon} />
          <p className={styles.isFreeTitle}>
            {t('paymentMethods.isFreeText')}
            {' '}
            <span>{t('paymentMethods.isFree')}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={paymentMethodPicker}>
      <div className={paymentMethodContainer}>
        <Grid container spacing={isDeviceMdSize ? 2 : 4}>
          {!isFreePayment && savedCards.map((card) => {
            const isSelected = selectedMethod && selectedMethod.savedCard === card.cardHolderID;

            const paymentMethodClass = cn({
              [paymentMethod]: true,
              [selected]: isSelected,
              [redBorder]: !isValid && !isSelected,
            });

            return (
              <Grid key={card.cardHolderID} item xs={12} md={6}>
                <div
                  className={paymentMethodClass}
                  onClick={() => selectMethod({ savedCard: card.cardHolderID })}
                  role="button"
                  tabIndex="0"
                >
                  <div className={styles.cardContainer}>
                    <img alt="card" src={getCardIcon(card.cardPan)} />
                    <div>
                      <Typography className={styles.cardNumber}>{card.cardPan}</Typography>
                      <Typography className={styles.validUntil}>
                        {t('paymentMethods.expDate')}
                        {' '}
                        {formatExpirationString(card.expDate)}
                      </Typography>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => cardRemoveHandler(e, card)}
                        className={styles.removeCard}
                      >
                        {t('paymentMethods.removeCard')}
                      </div>
                    </div>
                  </div>

                  <CheckCircleIcon
                    className={isSelected ? styles.checkedIcon : styles.uncheckedIcon}
                  />
                </div>
              </Grid>
            );
          })}
          {paymentMethodList.map((method) => {
            const { label, icon } = method;
            const isSelected = method.label === selectedMethod;
            const isDisabled = isFreePayment && method.label !== PAYMENT_METHOD.FREE;

            const paymentMethodClass = cn({
              [paymentMethod]: true,
              [selected]: isSelected,
              [redBorder]: !isValid && !isSelected,
              [disabled]: isDisabled,
              [ameriaCardMethod]: method.label === PAYMENT_METHOD.CARD,
            });

            return (
              <Grid key={label} item xs={12} md={6}>
                <div
                  onClick={() => selectMethod(label)}
                  className={paymentMethodClass}
                  role="button"
                  tabIndex="0"
                >
                  <div>
                    <div className={styles.iconContainer}>{icon}</div>
                  </div>
                  <CheckCircleIcon
                    className={isSelected ? styles.checkedIcon : styles.uncheckedIcon}
                  />
                </div>
              </Grid>
            );
          })}
        </Grid>
      </div>
      {!isValid && (
        <div className={error}>{t('validationErrors.paymentNotSelected')}</div>
      )}
    </div>
  );
};

PaymentMethodPicker.propTypes = {
  selectMethod: PropTypes.func.isRequired,
  selectedMethod: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ savedCard: PropTypes.string }),
  ]),
  savedCards: PropTypes.arrayOf(
    PropTypes.shape({
      cardHolderID: PropTypes.string.isRequired,
      cardPan: PropTypes.string.isRequired,
      expDate: PropTypes.string.isRequired,
    }),
  ),
  isValid: PropTypes.bool,
  isFreePayment: PropTypes.bool,
  isAutomaticRecurringPayment: PropTypes.bool,
  isPolicyAttachCard: PropTypes.bool,
};

PaymentMethodPicker.defaultProps = {
  selectedMethod: '',
  savedCards: [],
  isValid: true,
  isFreePayment: false,
  isAutomaticRecurringPayment: false,
  isPolicyAttachCard: false,
};

export default PaymentMethodPicker;
